import * as z from "zod";
import type { SupplyIntelBrandUnitGroup } from "./brand-units";
import type { SupplyIntelConfig } from "./config";
import { SupplyIntelDepartment } from "./department";
import { SupplyIntelGradeStatus } from "./next-step";

const SHOPIFY_PRODUCT_GID = /^gid:\/\/shopify\/Product\/\d+$/;
const SHOPIFY_VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/\d+$/;
/** Rithum's image slots. */
const MAX_IMAGES = 12;

/** The condition a listing uses, from the brand's grade setting; Relist maps it to each channel's codes. */
export enum SupplyIntelNormalizedCondition {
  New = "new",
  PreOwned = "pre_owned",
}

const SupplyIntelUnitAvailableSchema_ = z.object({
  redoUnitId: z.string(),
  /** The unit's label in the warehouse: its inventory item ID. */
  licensePlate: z.string(),
  /** Increases with each export, so a receiver keeps the newest record. */
  version: z.number(),
  redoAccountId: z.string(),
  ownerRef: z.string(),
  /** Null until resale terms with the brand exist. */
  agreementRef: z.string().nullable(),
  product: z.object({
    shopifyProductGid: z.string().nullable(),
    shopifyVariantGid: z.string(),
    sku: z.string().nullable(),
    upc: z.string().nullable(),
  }),
  title: z.string().nullable(),
  /** The store product the variant belongs to: the key for grouping variants into one listing. */
  style: z.object({
    productFamilyId: z.string().nullable(),
    title: z.string().nullable(),
  }),
  /** The variant's options in the store's order, such as Size and Color. */
  options: z.array(z.object({ name: z.string(), value: z.string() })),
  size: z.string().nullable(),
  color: z.string().nullable(),
  weight: z.object({ value: z.number(), unit: z.string() }).nullable(),
  material: z.string().nullable(),
  /** Who the product is for, from its gender metafields; null when the store doesn't say. */
  department: z.enum(SupplyIntelDepartment).nullable(),
  brand: z.string().nullable(),
  /** The Shopify standard taxonomy path; null without one. */
  category: z.string().nullable(),
  /** The category's Shopify taxonomy GID, such as "gid://shopify/TaxonomyCategory/aa-1-13-8"; null outside Shopify's taxonomy. */
  categoryGid: z.string().nullable(),
  /** The store's own product type, such as "Leggings"; null when unset. Often set when `category` is null. */
  productType: z.string().nullable(),
  /** The product's store description as plain text, at most 4,000 characters; null when it has none. */
  description: z.string().nullable(),
  /** The product's store tags, often merchandising labels rather than attributes. */
  tags: z.array(z.string()),
  rawGrade: z.string().nullable(),
  normalizedCondition: z.enum(SupplyIntelNormalizedCondition),
  /** What the warehouse noted when it checked the unit on arrival, if anything. */
  conditionNotes: z.string().nullable(),
  /** Photos of this unit from its arrival check; most units have none, so listings fall back to `productImages`. */
  photos: z.array(z.string()),
  productImages: z.array(z.string()),
  warehouseRef: z.string(),
  currency: z.string(),
  /** The store price: the most a listing should ask. */
  maxPrice: z.number(),
  /** The sale price the estimate expects, at the brand's resale price share. */
  suggestedPrice: z.number(),
  /** Below this, liquidating the unit recovers more than selling it. */
  priceFloor: z.number(),
  allowedChannels: z.array(z.string()),
  /** When the listing window ends and the unit moves to its next outcome. */
  deadline: z.string(),
  pooling: z.enum(["auto", "single"]),
  receivedAt: z.string(),
});
/** One unit ready to list, in the Redo↔Relist `UnitAvailable` shape plus what a listing needs. */
export const SupplyIntelUnitAvailableSchema: z.ZodType<SupplyIntelUnitAvailable> =
  SupplyIntelUnitAvailableSchema_;
export interface SupplyIntelUnitAvailable extends z.infer<
  typeof SupplyIntelUnitAvailableSchema_
> {}

function roundCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function gidOrNull(value: string | null, pattern: RegExp): string | null {
  return value !== null && pattern.test(value) ? value : null;
}

/** A variant option's value when its name is one of `names`, such as Size or Colour. */
function optionValue(
  options: readonly { name: string; value: string }[],
  names: RegExp,
): string | null {
  return options.find((option) => names.test(option.name))?.value ?? null;
}

/**
 * A `UnitAvailable` record for one unit the dashboard recommends reselling.
 * Prices are in the unit's own currency. Null when the unit can't be listed:
 * no price, no Shopify variant (a pool's key), or no warehouse (where it ships from).
 */
export function supplyIntelUnitAvailable({
  accountId,
  config,
  details,
  check,
  group,
  productImages,
  unit,
  version,
  nowMs,
}: {
  accountId: string;
  /** The brand's config, with its own resale prices where it has them. */
  config: SupplyIntelConfig;
  group: Pick<
    SupplyIntelBrandUnitGroup,
    | "category"
    | "currency"
    | "grade"
    | "gradeStatus"
    | "listPrice"
    | "productFamilyId"
    | "productFamilyTitle"
    | "title"
    | "sku"
    | "warehouseId"
  >;
  productImages: readonly string[];
  details: {
    options: readonly { name: string; value: string }[];
    weight: { value: number; unit: string } | null;
    material: string | null;
    department: SupplyIntelDepartment | null;
    productType: string | null;
    description: string | null;
    tags: readonly string[];
  };
  check: { photos: readonly string[]; notes: string | null };
  unit: {
    inventoryItemId: string;
    receivedAt: Date;
    variantSourceId: string | null;
    familySourceId: string | null;
    upc: string | null;
    vendor: string | null;
  };
  version: number;
  nowMs: number;
}): SupplyIntelUnitAvailable | null {
  const shopifyVariantGid = gidOrNull(
    unit.variantSourceId,
    SHOPIFY_VARIANT_GID,
  );
  if (
    group.listPrice === null ||
    group.currency === null ||
    shopifyVariantGid === null ||
    group.warehouseId === null
  ) {
    return null;
  }
  const preOwned = group.gradeStatus === SupplyIntelGradeStatus.NotSoldAsNew;
  const share = preOwned
    ? config.estimate.preOwnedSalePriceShareOfList
    : config.estimate.salePriceShareOfList;
  return {
    redoUnitId: unit.inventoryItemId,
    licensePlate: unit.inventoryItemId,
    version,
    redoAccountId: accountId,
    ownerRef: accountId,
    agreementRef: null,
    product: {
      shopifyProductGid: gidOrNull(unit.familySourceId, SHOPIFY_PRODUCT_GID),
      shopifyVariantGid,
      sku: group.sku,
      upc: unit.upc,
    },
    title: group.title,
    style: {
      productFamilyId: group.productFamilyId,
      title: group.productFamilyTitle,
    },
    options: [...details.options],
    size: optionValue(details.options, /^size$/i),
    color: optionValue(details.options, /^colou?r$/i),
    weight: details.weight,
    material: details.material,
    department: details.department,
    brand: unit.vendor,
    category: group.category?.name ?? null,
    categoryGid: group.category?.shopifyId
      ? `gid://shopify/TaxonomyCategory/${group.category.shopifyId}`
      : null,
    productType: details.productType,
    description: details.description,
    tags: [...details.tags],
    rawGrade: group.grade,
    normalizedCondition: preOwned
      ? SupplyIntelNormalizedCondition.PreOwned
      : SupplyIntelNormalizedCondition.New,
    conditionNotes: check.notes,
    photos: [...check.photos],
    productImages: productImages.slice(0, MAX_IMAGES),
    warehouseRef: group.warehouseId,
    currency: group.currency,
    maxPrice: roundCents(group.listPrice),
    suggestedPrice: roundCents(group.listPrice * share),
    priceFloor: roundCents(
      group.listPrice * config.estimate.liquidationShareOfList,
    ),
    allowedChannels: ["ebay"],
    deadline: new Date(
      nowMs + config.resaleWindowDays * 24 * 60 * 60 * 1000,
    ).toISOString(),
    pooling: "auto",
    receivedAt: unit.receivedAt.toISOString(),
  };
}
